<template>
  <div
    id="teams-list"
    :key="canCreateTeam"
  >
    <h1>Hackathon Teams</h1>
    <v-skeleton-loader
      v-if="loading"
      type="table"
    />
    <div
      v-else
    >
      <p class="teams-stats">
        <!-- eslint-disable-next-line max-len -->
        There are <strong>{{ participantsCount }} participants</strong> across <strong>{{ teamsCount }} teams</strong>.
      </p>
      <p
        v-if="isSetup"
        class="teams-stats"
      >
        Registration period not yet started, check back for updates!
      </p>
      <div
        class="button-wrapper"
      >
        <v-tooltip
          bottom
          color="rgba(0, 78, 125, 1.0)"
        >
          <!-- eslint-disable-next-line vue/no-unused-vars -->
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="!isJudging"
              id="hide-full-teams-button"
              class="list-view-button"
              icon
              :outlined="false"
              :title="fullTeamsTitle"
              @click="toggleFullTeamsHidden()"
            >
              {{ fullTeamsTitle }}
            </v-btn>
          </template>
          <span>{{ fullTeamsTitle }}</span>
        </v-tooltip>
        <v-tooltip
          bottom
          color="rgba(0, 78, 125, 1.0)"
        >
          <!-- eslint-disable-next-line vue/no-unused-vars -->
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="isJudging"
              id="hide-already-voted-teams-button"
              class="list-view-button"
              icon
              :outlined="false"
              :title="alreadyVotedTeamsTitle"
              @click="toggleAlreadyVotedTeamsHidden()"
            >
              {{ alreadyVotedTeamsTitle }}
            </v-btn>
          </template>
          <span>{{ alreadyVotedTeamsTitle }}</span>
        </v-tooltip>
        <v-tooltip
          bottom
          color="rgba(0, 78, 125, 1.0)"
        >
          <!-- eslint-disable-next-line vue/no-unused-vars -->
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              id="show-list-view"
              class="list-view-button"
              icon
              :outlined="false"
              title="List view"
              @click="showListView()"
            >
              <span class="material-symbols-rounded">
                list
              </span>
            </v-btn>
          </template>
          <span>Show list view</span>
        </v-tooltip>
        <v-tooltip
          bottom
          color="rgba(0, 78, 125, 1.0)"
        >
          <!-- eslint-disable-next-line vue/no-unused-vars -->
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              id="show-grid-view"
              class="grid-view-button"
              icon
              :outlined="false"
              title="Grid view"
              @click="showGridView()"
            >
              <span class="material-symbols-rounded">
                grid_view
              </span>
            </v-btn>
          </template>
          <span>Show grid view</span>
        </v-tooltip>
        <v-dialog
          v-if="canCreateTeam"
          v-model="dialog"
          width="500"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="create-team-button"
              v-bind="attrs"
              v-on="on"
            >
              Create team&nbsp;&nbsp;<span class="material-symbols-rounded">add</span>
            </v-btn>
          </template>

          <v-card>
            <v-card-title>
              Create a new Team
            </v-card-title>
            <team-add
              :current-user="currentUser"
              :teams-count="teamsCount"
              @closeDialog="() => {dialog = false}"
            />
          </v-card>
        </v-dialog>
      </div>
      <div
        v-if="filteredTeams.length > 0"
      >
        <team
          v-for="(thisTeam, index) in filteredTeams"
          :key="index"
          :current-user="currentUser"
          :team="thisTeam"
          :team-index="index"
          :event-state="selectedEvent.state"
          :selected-event="selectedEvent"
          :is-list-view="isListView"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Team from '@/components/Team.vue';
import TeamAdd from '@/components/TeamAdd.vue';
import TeamsClient from '@/api/TeamsClient';
import VotingClient from '@/api/VotingClient';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'TeamsList',
  components: {
    Team,
    TeamAdd,
  },
  props: {
    currentUser: {
      type: Object,
      default: () => {},
    },
    selectedEvent: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      dialog: false,
      isListView: false,
      canCreateTeam: false,
      allTeams: [],
      allVotes: [],
      loading: true,
      fullTeamsHidden: false,
      alreadyVotedTeamsHidden: false,
    };
  },
  computed: {
    ...mapGetters({
      currentJwtToken: 'currentJwtToken',
      currentHackathonId: 'currentHackathonId',
      haveTeamsListView: 'haveTeamsListView',
      isAuthorized: 'isAuthorized',
    }),
    isSetup() {
      return this.selectedEvent.state === 'Setup';
    },
    isJudging() {
      return this.selectedEvent.state === 'Judging';
    },
    teamsCount() {
      return this.allTeams.length;
    },
    participantsCount() {
      let totalParticipants = 0;
      this.allTeams.forEach((team) => {
        totalParticipants += team.members.length;
      });
      return totalParticipants;
    },
    fullTeamsTitle() {
      return this.fullTeamsHidden ? 'Show Full Teams' : 'Hide Full Teams';
    },
    alreadyVotedTeamsTitle() {
      return this.alreadyVotedTeamsHidden ? 'Show Already Voted' : 'Hide Already Voted';
    },
    filteredTeams() {
      let teamsToShow = this.allTeams;
      if (this.fullTeamsHidden) {
        teamsToShow = teamsToShow.filter((team) => !team.full);
      }
      if (this.alreadyVotedTeamsHidden) {
        teamsToShow = teamsToShow.filter((team) => !team.haveAlreadyVotedOnThisTeam);
      }
      return teamsToShow;
    },
  },
  mounted() {
    this.setCanCreateTeam();
    this.getTeamsData();
    this.getVotesData();
    this.isListView = this.haveTeamsListView;
  },
  methods: {
    ...mapActions({
      setUserTeam: 'setUserTeam',
      setTeamsListView: 'setTeamsListView',
    }),
    async showGridView() {
      this.isListView = false;
      await this.setTeamsListView(false);
    },
    async showListView() {
      this.isListView = true;
      await this.setTeamsListView(true);
    },
    toggleFullTeamsHidden() {
      this.fullTeamsHidden = !this.fullTeamsHidden;
    },
    toggleAlreadyVotedTeamsHidden() {
      this.alreadyVotedTeamsHidden = !this.alreadyVotedTeamsHidden;
    },
    setCanCreateTeam() {
      this.canCreateTeam = (this.selectedEvent.state === 'Registration')
        && this.isAuthorized
        && this.currentUser.team === ''
        && !this.currentUser.isAdmin;
    },
    async getTeamsData() {
      this.loading = true;
      /* eslint-disable-next-line max-len */
      const teamsClient = new TeamsClient(this.currentJwtToken, this.selectedEvent.id);
      const responseData = await teamsClient.getTeams();
      this.allTeams = [...responseData];
      this.$store.state.teams = this.allTeams;
      this.allTeams.forEach((team) => {
        team.members.forEach((member) => {
          if (member.email === this.currentUser.email) {
            this.setUserTeam(team.id);
          }
        });
      });
      this.setCanCreateTeam();
      this.loading = false;
    },
    async getVotesData() {
      const eventId = this.$store.state.hackathonID || '';
      if (eventId !== '') {
        /* eslint-disable-next-line max-len */
        const votingClient = await new VotingClient(this.currentJwtToken, this.currentHackathonId);
        const responseData = await votingClient.getVotes();
        this.allVotes = [...responseData];
        this.$store.state.votes = this.allVotes;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#teams-list {
  margin-top: 2em;
  position: relative;
}

.teams-stats {
  margin-bottom: 1em;
}

.v-card__title {
  color: #f26522;
}

.button-wrapper {
  position: absolute;
  right: 1.5em;
  margin-top: -4em;
}

@media (max-width: 999px) {
  .button-wrapper {
    display: none;
  }
}

.create-team-button {
  background-color: #004e7d !important;
  color: #fff !important;
  margin-left: 1em;
}

.grid-view-button,
.list-view-button {
  font-size: 1.15em !important;
  color: #004e7d !important;
  padding: 0.25em !important;
}

#hide-full-teams-button {
   width: 190px;
}

#hide-already-voted-teams-button {
   width: 220px;
}
</style>
